import { FC, ChangeEvent, useMemo, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { TradingOffer } from '../../../../../types/TradingOffer';

type TradingOfferActionCardProps = {
  offer: TradingOffer;
  availableShares: number;
  purchaseQuantity: number;
  isExpired: boolean;
  isOwnOffer: boolean;
  hasPartnerBuyer: boolean;
  /* eslint-disable-next-line no-unused-vars */
  onQuantityChange: (quantity: number) => void;
  onBuyShares: () => void;
  onViewOtherOffers: () => void;
};

const TradingOfferActionCard: FC<TradingOfferActionCardProps> = ({
  offer,
  availableShares,
  purchaseQuantity,
  isExpired,
  isOwnOffer,
  hasPartnerBuyer,
  onQuantityChange,
  onBuyShares,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const [inputValue, setInputValue] = useState<string>('0');

  const isSellOffer = offer.type === 'SELL';

  const mustBuyAll = !offer.isPartialAmountEnabled;

  const minPurchaseQuantity = useMemo(() => {
    if (mustBuyAll) return availableShares;
    return Math.max(
      1,
      Math.min(offer.minSharesPerAcceptance ?? 1, availableShares)
    );
  }, [availableShares, mustBuyAll, offer.minSharesPerAcceptance]);

  const inputError = useMemo(() => {
    if (mustBuyAll || !inputValue) return null;
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed === 0) return null;
    if (parsed < minPurchaseQuantity) {
      return t('MinSharesPerAcceptanceError', {
        min: formatNumber(minPurchaseQuantity),
      });
    }
    if (parsed > availableShares) {
      return t('MaxSharesExceededError', {
        max: formatNumber(availableShares),
      });
    }
    return null;
  }, [mustBuyAll, inputValue, minPurchaseQuantity, availableShares, t]);

  const parsedInputValue = parseInt(inputValue, 10);
  const isInputValid = mustBuyAll
    ? availableShares > 0
    : !inputError && !isNaN(parsedInputValue) && parsedInputValue > 0;

  const isBuyButtonDisabled = useMemo(
    () => isOwnOffer || !hasPartnerBuyer || !isInputValid,
    [isOwnOffer, hasPartnerBuyer, isInputValid]
  );

  const subtotal = useMemo(
    () => purchaseQuantity * offer.sharePrice,
    [purchaseQuantity, offer.sharePrice]
  );

  const total = useMemo(() => subtotal, [subtotal]);

  const quickAmounts = useMemo(() => {
    if (mustBuyAll) {
      return availableShares > 0 ? [availableShares] : [];
    }

    const amounts: number[] = [];
    if (availableShares >= 10) amounts.push(10);
    if (availableShares >= 25) amounts.push(25);
    if (availableShares > 0) amounts.push(availableShares);
    const filtered = amounts.filter((a) => a >= minPurchaseQuantity);

    const uniqueSorted = [...new Set(filtered)].sort((a, b) => a - b);

    return uniqueSorted;
  }, [availableShares, mustBuyAll, minPurchaseQuantity]);

  const handleIncrement = () => {
    if (!mustBuyAll && purchaseQuantity < availableShares) {
      const newValue = purchaseQuantity + 1;
      setInputValue(String(newValue));
      onQuantityChange(newValue);
    }
  };

  const handleDecrement = () => {
    if (!mustBuyAll && purchaseQuantity > minPurchaseQuantity) {
      const newValue = purchaseQuantity - 1;
      setInputValue(String(newValue));
      onQuantityChange(newValue);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);

    const value = parseInt(raw, 10);
    if (
      !isNaN(value) &&
      value >= minPurchaseQuantity &&
      value <= availableShares
    ) {
      onQuantityChange(value);
    }
  };

  const handleQuickAmount = (amount: number) => {
    setInputValue(String(amount));
    onQuantityChange(amount);
  };

  if (isExpired) {
    return (
      <div
        className="card card-bordered"
        style={{ position: 'sticky', top: '20px' }}
      >
        <div className="card-inner text-center py-4">
          <p className="text-soft mb-3">{t('ExpiredOfferActionDescription')}</p>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onViewOtherOffers}
          >
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    );
  }

  const showPartialMinHint =
    offer.isPartialAmountEnabled && minPurchaseQuantity > 1;

  return (
    <div
      className="card card-bordered"
      style={{ position: 'sticky', top: '20px' }}
    >
      <div className="card-inner">
        <div className="text-center mb-4">
          <em className="icon ni ni-cart fs-24px text-success" />
          <h5 className="mt-2 mb-1">
            {isSellOffer ? t('BuyShares') : t('SellShares')}
          </h5>
          <p className="text-soft fs-13px mb-0">
            {isOwnOffer
              ? t('OwnOfferDescription')
              : t('AcceptOfferDescription')}
          </p>
        </div>

        <hr />

        <div
          className={`form-group mb-3 ${
            isOwnOffer ? 'is-disable cursor-not-allowed' : ''
          }`}
        >
          <label className="form-label" htmlFor="purchase-quantity">
            {t('HowManyShares')}
          </label>

          <div className="d-flex w-100 mb-2" style={{ gap: '6px' }}>
            {quickAmounts.map((amount) => (
              <Button
                key={amount}
                variant={purchaseQuantity === amount ? 'primary' : 'light'}
                onClick={() => handleQuickAmount(amount)}
                disabled={isOwnOffer || mustBuyAll}
                dimStyle={!!(purchaseQuantity === amount)}
                className="flex-fill"
              >
                <span className="text-center">
                  {amount === availableShares
                    ? `${t('All')} (${formatNumber(amount)})`
                    : formatNumber(amount)}
                </span>
              </Button>
            ))}
          </div>

          <div className="input-group">
            <Button
              disabled={
                isOwnOffer ||
                mustBuyAll ||
                purchaseQuantity <= minPurchaseQuantity
              }
              onClick={handleDecrement}
              outlineStyle
              variant={ButtonVariants.SECONDARY}
            >
              −
            </Button>
            <input
              id="purchase-quantity"
              type="number"
              className={`form-control text-center${
                inputError ? ' is-invalid' : ''
              }`}
              value={mustBuyAll ? availableShares : inputValue}
              onChange={handleInputChange}
              disabled={isOwnOffer}
              readOnly={mustBuyAll}
              min={minPurchaseQuantity}
              max={availableShares}
            />
            <Button
              disabled={
                isOwnOffer || mustBuyAll || purchaseQuantity >= availableShares
              }
              onClick={handleIncrement}
              outlineStyle
              variant={ButtonVariants.SECONDARY}
            >
              +
            </Button>
          </div>

          {mustBuyAll && (
            <span className="form-note mt-1 d-block text-warning">
              {t('MustBuyAllSharesNote')}
            </span>
          )}

          {showPartialMinHint && (
            <span
              className={`form-note mt-1 d-block ${
                inputError ? 'text-danger' : ''
              }`}
            >
              {t('MinSharesPerAcceptanceNote')}{' '}
              <strong>{formatNumber(minPurchaseQuantity)}</strong>
            </span>
          )}

          <span className="form-note mt-1 d-block">
            {t('MaxAvailable')}: {formatNumber(availableShares)}{' '}
            {t('Shares').toLowerCase()}
          </span>
        </div>

        <hr />

        <div className="mb-4">
          <h6 className="overline-title text-soft mb-3">
            {t('PurchaseSummary')}
          </h6>

          <div className="d-flex justify-content-between py-2">
            <span className="text-soft">{t('Shares')}</span>
            <strong>{formatNumber(purchaseQuantity)}</strong>
          </div>
          <div className="d-flex justify-content-between py-2">
            <span className="text-soft">{t('PricePerShare')}</span>
            <strong>{formatCurrency(offer.sharePrice)}</strong>
          </div>
          <div className="d-flex justify-content-between py-2 border-top">
            <span className="text-soft">{t('Subtotal')}</span>
            <strong>{formatCurrency(subtotal)}</strong>
          </div>
          <div className="d-flex justify-content-between py-3">
            <span className="fs-16px">{t('TotalToPay')}</span>
            <strong className="fs-20px text-success">
              {formatCurrency(total)}
            </strong>
          </div>
        </div>

        <div className="alert alert-light mb-4">
          <h6 className="alert-heading fs-13px">{t('AfterPurchase')}:</h6>
          <p className="mb-0 fs-13px">
            +{formatNumber(purchaseQuantity)} {t('Shares').toLowerCase()}
          </p>
        </div>

        <Button
          className="btn btn-lg btn-success btn-block mb-3"
          disabled={isBuyButtonDisabled}
          variant={ButtonVariants.SUCCESS}
          onClick={onBuyShares}
        >
          <em className="icon ni ni-check-circle mr-2" />
          {isSellOffer
            ? `${t('Buy')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`
            : `${t('Sell')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`}
        </Button>

        <div className="text-center">
          <Button
            className="btn btn-link text-soft fs-13px"
            onClick={onViewOtherOffers}
            variant={ButtonVariants.LINK}
          >
            <em className="icon ni ni-arrow-left mr-1" />
            {t('ViewOtherOffers')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TradingOfferActionCard;
